import type { PathLink } from '@/hooks/usePathHelper'
import type { DeliveryShareFilters } from '@/lib/services/delivery-shares/delivery-shares.schema'
import type { DeliveryRoutesSearch } from '@/components/orders/delivery-share.utils'
import type {
  DeliveryShare,
  DeliveryShareChannel,
  DeliveryShareIdInput,
  DeliveryShareRecipient,
  SendDeliveryShareInput,
} from '@/lib/services/delivery-shares/delivery-shares.types'

import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import LoadingButton from '@/components/ui/loading-button'
import DeliveryRouteDatePicker from '@/components/orders/DeliveryRouteDatePicker'
import DeliveryRouteOrderLink from '@/components/orders/DeliveryRouteOrderLink'
import { buildDeliveryRouteDisplayStops } from '@/components/orders/delivery-route-stops'
import DeliveryRouteShareDialog from '@/components/orders/DeliveryRouteShareDialog'
import {
  buildDeliveryRouteIndexSections,
  filterDeliveryRoutesByDate,
  formatDeliveryRouteDate,
  formatDeliveryRouteDateTime,
  getDeliveryRouteDateSummary,
  getDeliveryRouteLocationAddress,
  getDeliveryRouteMissedCount,
  getDeliveryRouteOrderLabel,
  getDeliveryRouteSelectionId,
  getDeliveryShareErrorMessage,
  isDeliveryRouteStarted,
  isDeliveryRouteCompleted,
} from '@/components/orders/delivery-share.utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatPhone } from '@/hooks/use-phone-formatter'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { canManageSalesOrderField } from '@/lib/sales-order-access'
import {
  deliveryRoutesSearchSchema,
  getDeliveryShareFilters,
} from '@/lib/services/delivery-shares/delivery-shares.schema'
import {
  deliveryShareQueries,
  useDeliveryShares,
  useRevokeDeliveryShareMutation,
  useSendDeliveryShareMutation,
} from '@/lib/services/delivery-shares/delivery-shares.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  type RegisteredRouter,
} from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import {
  AlertCircleIcon,
  ChevronDown,
  Copy,
  Route as RouteIcon,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

type SentRecipient = DeliveryShareRecipient & {
  displayName: string
  key: string
  channelLabel: string
}
// Resend keys also identify an in-flight saved-contact request, which has no destination field.
type RecipientIdentity = Pick<DeliveryShareRecipient, 'channel'> & {
  delivery_route_contact_id?: DeliveryShareRecipient['delivery_route_contact_id']
  destination?: Extract<
    SendDeliveryShareInput,
    { delivery_route_contact_id: number }
  >['destination']
}
type RouteTone = 'default' | 'attention'
type OnResend = (
  share: DeliveryShare,
  recipient: DeliveryShareRecipient,
  channel?: DeliveryShareChannel
) => void
interface RouteCardProps {
  deletePendingId?: DeliveryShareIdInput | null
  isReadOnly?: boolean
  onCopy: (shareUrl: string | null) => void | Promise<void>
  onDelete: (share: DeliveryShare) => void
  onResend?: OnResend
  resendPendingKey?: string | null
  viewLinkProps: (share: DeliveryShare) => PathLink
}
interface RouteCardListProps {
  cardProps: RouteCardProps
  shares: DeliveryShare[]
  tone?: RouteTone
}
type RouteSummaryCardProps = Omit<RouteCardProps, 'viewLinkProps'> & {
  share: DeliveryShare
  viewLink: PathLink
  tone?: RouteTone
}
type RouteSentRecipientsProps = Pick<RouteCardProps, 'isReadOnly' | 'resendPendingKey'> & {
  onResend: OnResend
  share: DeliveryShare
}

export function getDeliveryShareIndexFilters(
  search: DeliveryRoutesSearch | null | undefined
): DeliveryShareFilters {
  return getDeliveryShareFilters({
    salesOrderId: search?.salesOrderId,
  })
}

export function getDeletePendingId(
  revokeDeliveryShare:
    | Pick<ReturnType<typeof useRevokeDeliveryShareMutation>, 'isPending' | 'variables'>
    | null
    | undefined
): DeliveryShareIdInput | null | undefined {
  return revokeDeliveryShare?.isPending ? revokeDeliveryShare.variables : null
}

export function getDeliveryShareSentRecipients(
  share: Pick<DeliveryShare, 'share_recipients'> | null | undefined,
  { limit = 2 }: { limit?: number } = {}
): SentRecipient[] {
  const recipients = Array.isArray(share?.share_recipients) ? share.share_recipients : []
  const visibleRecipients: SentRecipient[] = []
  const seen = new Set<string>()

  recipients.forEach((recipient) => {
    const displayName =
      recipient?.name ||
      getDeliveryShareDestinationDisplay(recipient?.destination, recipient?.channel)
    const key = recipient?.delivery_route_contact_id
      ? `contact:${recipient.delivery_route_contact_id}`
      : `destination:${recipient?.channel || ''}:${recipient?.destination || displayName}`

    if (!displayName || seen.has(key)) {
      return
    }

    visibleRecipients.push({
      ...recipient,
      displayName,
      key,
      channelLabel: getDeliveryShareChannelLabel(recipient?.channel),
    })
    seen.add(key)
  })

  return Number.isFinite(limit) ? visibleRecipients.slice(0, limit) : visibleRecipients
}

export function getDeliveryShareSentRecipientsLabel(
  share: Pick<DeliveryShare, 'share_recipients'> | null | undefined
): string | null {
  const recipients = getDeliveryShareSentRecipients(share, { limit: Infinity })

  const [firstRecipient] = recipients

  if (!firstRecipient) {
    return null
  }
  const overflowCount = recipients.length - 1

  return overflowCount > 0
    ? `Sent to ${firstRecipient.displayName} +${overflowCount}`
    : `Sent to ${firstRecipient.displayName}`
}

export function getDeliveryShareRecipientResendKey(
  deliveryShareId: DeliveryShareIdInput | null | undefined,
  recipient: RecipientIdentity | null | undefined,
  channel = recipient?.channel
): string | null {
  if (!deliveryShareId || !recipient) {
    return null
  }

  const recipientKey = recipient.delivery_route_contact_id
    ? `contact:${recipient.delivery_route_contact_id}:${channel || ''}`
    : `destination:${channel || recipient.channel || ''}:${recipient.destination || ''}`

  return `${deliveryShareId}:${recipientKey}`
}

function getDeliveryShareChannelLabel(channel: DeliveryShareChannel | undefined): string {
  return channel === 'sms' ? 'Text' : 'Email'
}

function getDeliveryShareChannelActionLabel(channel: DeliveryShareChannel): string {
  return channel === 'sms' ? 'text' : 'email'
}

function getDeliveryShareDestinationDisplay(
  destination: string | null | undefined,
  channel: DeliveryShareChannel | undefined
): string {
  if (!destination) {
    return ''
  }

  return channel === 'sms' ? formatPhone(destination) : destination
}

function getDeliveryShareRecipientContactDestination(
  recipient: DeliveryShareRecipient | null | undefined,
  channel: DeliveryShareChannel
): string {
  if (channel === 'sms') {
    return recipient?.contact?.phone || ''
  }

  return recipient?.contact?.email || ''
}

function getDeliveryShareRecipientDestination(
  recipient: DeliveryShareRecipient | null | undefined,
  channel = recipient?.channel
): string {
  if (!recipient || !channel) {
    return ''
  }

  const contactDestination = getDeliveryShareRecipientContactDestination(recipient, channel)

  if (contactDestination) {
    return contactDestination
  }

  return recipient.channel === channel ? recipient.destination || '' : ''
}

function getDeliveryShareRecipientAlternateChannel(
  recipient: DeliveryShareRecipient | null | undefined
): DeliveryShareChannel | null {
  const alternateChannel = recipient?.channel === 'sms' ? 'email' : 'sms'

  return getDeliveryShareRecipientContactDestination(recipient, alternateChannel)
    ? alternateChannel
    : null
}

export function getDeliveryShareRecipientSendPayload(
  share: Pick<DeliveryShare, 'id'> | null | undefined,
  recipient: DeliveryShareRecipient | null | undefined,
  channel = recipient?.channel
): (SendDeliveryShareInput & { deliveryShareId: DeliveryShareIdInput }) | null {
  if (!share?.id || !recipient || !channel) {
    return null
  }

  const contactDestination = getDeliveryShareRecipientContactDestination(recipient, channel)

  if (recipient.delivery_route_contact_id && contactDestination) {
    return {
      deliveryShareId: share.id,
      channel,
      delivery_route_contact_id: recipient.delivery_route_contact_id,
    }
  }

  if (recipient.channel === channel && recipient.destination) {
    return {
      deliveryShareId: share.id,
      channel,
      destination: recipient.destination,
    }
  }

  return null
}

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/delivery-routes/')({
  component: DeliveriesIndexPage,
  validateSearch: (search) => deliveryRoutesSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('list', 'sales-orders')) throw notFound()

    const isReadOnly = !canManageSalesOrderField(context.ability, 'fulfillment')
    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

    if (isGlobalOrg) {
      return { isReadOnly }
    }

    const shareFilters = getDeliveryShareIndexFilters(search)

    await Promise.all([
      context.queryClient.ensureQueryData(
        deliveryShareQueries.index({
          organizationId: params.orgId,
          filters: shareFilters,
        })
      ),
      ...(search.salesOrderId
        ? [
            context.queryClient.ensureQueryData(
              salesOrderQueries.show({
                organizationId: params.orgId,
                salesOrderId: search.salesOrderId,
              })
            ),
          ]
        : []),
    ])

    return { isReadOnly }
  },
})

function RouteSection({
  cardProps,
  description,
  shares,
  title,
  tone = 'default',
}: RouteCardListProps & { description: string; title: string }) {
  if (shares.length === 0) {
    return null
  }

  const isAttentionSection = tone === 'attention'

  return (
    <section className="space-y-4">
      <div
        className={cn(
          'space-y-2',
          isAttentionSection &&
            'rounded-r-xl border-l-4 border-l-status-destructive-foreground bg-status-destructive-background px-4 py-3'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          {isAttentionSection ? (
            <AlertCircleIcon className="size-4 text-status-destructive-foreground" />
          ) : null}
          <Typography variant="h5">{title}</Typography>
          <Badge variant="secondary">
            {shares.length} route{shares.length === 1 ? '' : 's'}
          </Badge>
        </div>
        <Typography variant="supporting">{description}</Typography>
      </div>

      <RouteCardList shares={shares} cardProps={cardProps} tone={tone} />
    </section>
  )
}

export function RouteCardList({ cardProps, shares, tone = 'default' }: RouteCardListProps) {
  return (
    <div className="space-y-4">
      {shares.map((share) => (
        <RouteSummaryCard
          key={share.id}
          share={share}
          viewLink={cardProps.viewLinkProps(share)}
          deletePendingId={cardProps.deletePendingId}
          isReadOnly={cardProps.isReadOnly}
          onCopy={cardProps.onCopy}
          onDelete={cardProps.onDelete}
          onResend={cardProps.onResend}
          resendPendingKey={cardProps.resendPendingKey}
          tone={tone}
        />
      ))}
    </div>
  )
}

function RouteSummaryCard({
  deletePendingId,
  isReadOnly,
  onCopy,
  onDelete,
  onResend = () => {},
  resendPendingKey,
  share,
  viewLink,
  tone,
}: RouteSummaryCardProps) {
  const path = usePathHelper()
  const isDeleting = deletePendingId === share.id
  const isComplete = isDeliveryRouteCompleted(share)
  const isStarted = isDeliveryRouteStarted(share)
  const missedCount = getDeliveryRouteMissedCount(share)
  const displayStops = buildDeliveryRouteDisplayStops(share)
  const previewStops = displayStops.slice(0, 3)
  const remainingStopCount = Math.max(
    Number(share.stop_count ?? displayStops.length) - previewStops.length,
    0
  )
  const routeStatusSummary = share.completed_at
    ? `Completed ${formatDeliveryRouteDateTime(share.completed_at)}`
    : null
  const locationLink = share.location?.id ? path.locations.show(share.location.id).link : null
  const routeDateSummary = getDeliveryRouteDateSummary(share)
  const routeName = String(share.name ?? '').trim()
  const routeTitle = routeName || routeDateSummary
  const routeStopLabel = `${share.stop_count} stop${share.stop_count === 1 ? '' : 's'}`

  return (
    <Card
      interactive
      rail={tone === 'attention' ? 'destructive' : 'none'}
      size="sm"
      surface={isComplete ? 'subtle' : 'default'}
      className="relative isolate cursor-pointer"
    >
      <Link
        {...viewLink}
        aria-label={`Open route for ${routeTitle} with ${routeStopLabel}`}
        className="absolute inset-0 z-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      />
      <CardHeader
        size="sm"
        className="pointer-events-none relative z-10 gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:gap-x-4 xl:gap-y-1.5 [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=button]]:pointer-events-auto"
      >
        <CardTitle size="sm" className="min-w-0 flex flex-wrap items-center gap-2">
          {routeTitle}
          <Badge variant={isComplete ? 'success' : 'brand'}>
            {isComplete ? 'Completed' : 'Active'}
          </Badge>
          {isStarted ? <Badge variant="info">Started</Badge> : null}
        </CardTitle>
        <CardDescription size="sm" className="min-w-0 space-y-1">
          {routeName ? <span className="block">{routeDateSummary}</span> : null}
          {locationLink ? (
            <Link {...locationLink} className="inline-block hover:underline underline-offset-4">
              {getDeliveryRouteLocationAddress(share.location)}
            </Link>
          ) : (
            <span className="inline-block">{getDeliveryRouteLocationAddress(share.location)}</span>
          )}
          {routeStatusSummary ? <span className="block">{routeStatusSummary}</span> : null}
        </CardDescription>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center xl:col-start-2 xl:row-span-2 xl:row-start-1 xl:self-start xl:justify-self-end">
          <DeliveryRouteShareDialog
            share={share}
            disabled={isReadOnly}
            buttonVariant="outline"
            buttonSize="sm"
            className="w-full justify-center sm:w-auto"
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-center sm:w-auto"
            onClick={() => {
              void onCopy(share.share_url)
            }}
          >
            <Copy className="size-4" />
            Copy Link
          </Button>
          <ConfirmationDialog
            title="Delete delivery route?"
            description="Anyone using this route link will lose access immediately."
            confirmText="Delete route"
            pendingText="Deleting..."
            isPending={isDeleting}
            onConfirm={() => onDelete(share)}
            trigger={
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-center sm:w-auto"
                disabled={isReadOnly || isDeleting || isComplete}
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            }
          />
        </div>
      </CardHeader>
      <CardContent
        size="sm"
        className="pointer-events-none relative z-10 space-y-3 [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=button]]:pointer-events-auto"
      >
        <RouteSentRecipientsPanel
          isReadOnly={isReadOnly}
          onResend={onResend}
          resendPendingKey={resendPendingKey}
          share={share}
        />

        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">
            {share.stop_count} stop{share.stop_count === 1 ? '' : 's'}
          </Badge>
          <Badge variant={isComplete ? 'success' : 'brand'}>
            {share.delivered_count} delivered
          </Badge>
          {missedCount > 0 ? <Badge variant="warning">{missedCount} missed</Badge> : null}
          {share.attempt_count > 0 ? (
            <Badge variant="outline">
              {share.attempt_count} attempt{share.attempt_count === 1 ? '' : 's'}
            </Badge>
          ) : null}
        </div>

        {previewStops.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {previewStops.map((stop) =>
              stop.source_type === 'manual' ? (
                <Badge
                  key={getDeliveryRouteSelectionId(stop)}
                  variant={stop.is_delivered ? 'success' : 'outline'}
                >
                  {stop.recipient_name || 'Custom stop'}
                </Badge>
              ) : (
                <Badge
                  key={getDeliveryRouteSelectionId(stop)}
                  asChild
                  variant={stop.is_delivered ? 'success' : 'outline'}
                >
                  <DeliveryRouteOrderLink
                    order={stop}
                    searchParams={undefined}
                    className="hover:no-underline"
                  >
                    {getDeliveryRouteOrderLabel(stop)}
                  </DeliveryRouteOrderLink>
                </Badge>
              )
            )}
            {remainingStopCount > 0 ? (
              <Badge variant="outline">+{remainingStopCount} more</Badge>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function RouteSentRecipientsPanel({
  isReadOnly,
  onResend,
  resendPendingKey,
  share,
}: RouteSentRecipientsProps) {
  const recipients = getDeliveryShareSentRecipients(share, { limit: Infinity })
  const triggerLabel = getDeliveryShareSentRecipientsLabel(share)

  if (recipients.length === 0 || !triggerLabel) {
    return null
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="accent"
          size="sm"
          className="max-w-full justify-start sm:max-w-80"
        >
          <span className="min-w-0 truncate">{triggerLabel}</span>
          <ChevronDown className="size-3.5 text-interaction/75" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" size="wide" padding="none">
        <div className="space-y-3 p-3">
          <div className="flex items-center justify-between gap-3 border-b pb-2">
            <div className="min-w-0">
              <Typography variant="labelStrong" className="block">
                Shared route
              </Typography>
              <Typography variant="caption" className="block">
                {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
              </Typography>
            </div>
          </div>

          <div className="space-y-3">
            {recipients.map((recipient) => {
              const resendPayload = getDeliveryShareRecipientSendPayload(share, recipient)
              const resendKey = getDeliveryShareRecipientResendKey(
                share.id,
                recipient,
                recipient.channel
              )
              const isResendPending = resendPendingKey === resendKey
              const alternateChannel = getDeliveryShareRecipientAlternateChannel(recipient)
              const alternatePayload = alternateChannel
                ? getDeliveryShareRecipientSendPayload(share, recipient, alternateChannel)
                : null
              const alternateKey = alternateChannel
                ? getDeliveryShareRecipientResendKey(share.id, recipient, alternateChannel)
                : null
              const isAlternatePending = alternateKey ? resendPendingKey === alternateKey : false
              const destination = getDeliveryShareRecipientDestination(recipient)
              const destinationDisplay = getDeliveryShareDestinationDisplay(
                destination,
                recipient.channel
              )
              const sentLabel = `Sent by ${recipient.channelLabel.toLowerCase()}`

              return (
                <div
                  key={recipient.key}
                  className="space-y-2 border-b border-border/70 pb-3 last:border-b-0 last:pb-0"
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0 space-y-0.5">
                      <Typography variant="labelStrong" className="block truncate">
                        {recipient.displayName}
                      </Typography>
                      <Typography variant="caption" className="block">
                        {recipient.sent_at
                          ? `${sentLabel} ${formatDeliveryRouteDateTime(recipient.sent_at)}`
                          : sentLabel}
                      </Typography>
                    </div>
                    <Badge variant="outline" size="xs">
                      {recipient.channelLabel}
                    </Badge>
                  </div>

                  {destinationDisplay && destinationDisplay !== recipient.displayName ? (
                    <div className="min-w-0 rounded-md bg-accentSecondary/45 px-2.5 py-1.5">
                      <Typography variant="caption" className="block truncate">
                        {destinationDisplay}
                      </Typography>
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-2">
                    <LoadingButton
                      variant="outline"
                      size="xs"
                      isPending={isResendPending}
                      disabled={isReadOnly || !resendPayload || Boolean(resendPendingKey)}
                      onClick={() => onResend(share, recipient, recipient.channel)}
                    >
                      <Send className="size-3" />
                      Resend {getDeliveryShareChannelActionLabel(recipient.channel)}
                    </LoadingButton>
                    {alternateChannel && alternatePayload ? (
                      <LoadingButton
                        variant="outline"
                        size="xs"
                        isPending={isAlternatePending}
                        disabled={isReadOnly || Boolean(resendPendingKey)}
                        onClick={() => onResend(share, recipient, alternateChannel)}
                      >
                        <Send className="size-3" />
                        Send {getDeliveryShareChannelActionLabel(alternateChannel)}
                      </LoadingButton>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function DeliveriesIndexPage() {
  const isGlobalOrg = useIsGlobalOrg()

  return isGlobalOrg ? <GlobalDeliveryRoutesIndexPage /> : <FloristDeliveryRoutesIndexPage />
}

function GlobalDeliveryRoutesIndexPage() {
  const [showAlert, setShowAlert] = useState(true)

  return (
    <div className="space-y-4">
      {showAlert ? (
        <OrganizationContextNotice onDismiss={() => setShowAlert(false)}>
          Deliveries are florist-specific. Build and manage routes in the right florist account.
        </OrganizationContextNotice>
      ) : null}
    </div>
  )
}

function FloristDeliveryRoutesIndexPage() {
  const navigate = useNavigate<RegisteredRouter, typeof Route.fullPath>()
  const path = usePathHelper()
  const { isReadOnly } = Route.useLoaderData()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const [, copyToClipboard] = useCopyToClipboard()
  const selectedDueDate = search.due
  const isDateFiltered = Boolean(selectedDueDate)

  const shareFilters = getDeliveryShareIndexFilters(search)

  const { data: deliveryShares = [] } = useDeliveryShares({
    organizationId: orgId,
    filters: shareFilters,
    refetchInterval: 1000 * 60,
  })
  const focusedOrderQuery = useQuery({
    ...salesOrderQueries.show({
      organizationId: orgId,
      salesOrderId: search.salesOrderId,
    }),
    enabled: Boolean(search.salesOrderId),
  })
  const focusedOrder = focusedOrderQuery.data ?? null
  const revokeDeliveryShare = useRevokeDeliveryShareMutation()
  const sendDeliveryShare = useSendDeliveryShareMutation()
  const resendPendingKey = sendDeliveryShare.isPending
    ? getDeliveryShareRecipientResendKey(
        sendDeliveryShare.variables?.deliveryShareId,
        sendDeliveryShare.variables,
        sendDeliveryShare.variables?.channel
      )
    : null
  const groupedRoutes = buildDeliveryRouteIndexSections(deliveryShares)
  const filteredRoutes = selectedDueDate
    ? filterDeliveryRoutesByDate(deliveryShares, selectedDueDate)
    : []
  const routeSections = (
    [
      {
        key: 'pastDue',
        title: 'Delivery Date Passed',
        description: 'Routes from earlier delivery dates that still have stops left.',
        shares: groupedRoutes.pastDue,
        tone: 'attention',
      },
      {
        key: 'today',
        title: 'Today',
        description: 'Current delivery routes that still need attention today.',
        shares: groupedRoutes.today,
      },
      {
        key: 'upcoming',
        title: 'Upcoming',
        description: 'Future delivery routes that are ready before their delivery date.',
        shares: groupedRoutes.upcoming,
      },
      {
        key: 'undated',
        title: 'No Delivery Date',
        description: 'Active routes whose stops do not have a delivery date.',
        shares: groupedRoutes.undated,
      },
      {
        key: 'recentlyCompleted',
        title: 'Recently Completed',
        description:
          'Routes completed in the last few days, including routes that ended with missed stops.',
        shares: groupedRoutes.recentlyCompleted,
      },
    ] satisfies Array<{
      key: string
      title: string
      description: string
      shares: DeliveryShare[]
      tone?: RouteTone
    }>
  ).filter((section) => section.shares.length > 0)

  const handleDateChange = (due: string) => {
    void navigate({
      to: Route.to,
      search: (current) => ({
        ...current,
        due,
      }),
    })
  }

  const handleClearDateFilter = () => {
    void navigate({
      to: Route.to,
      search: (current) => ({
        ...current,
        due: undefined,
      }),
      replace: true,
    })
  }

  const handleCopy = async (shareUrl: string | null) => {
    if (!shareUrl) {
      return
    }

    await copyToClipboard(shareUrl)
    toast.success('Route link copied to clipboard')
  }

  const handleDelete = (share: DeliveryShare) => {
    revokeDeliveryShare.mutate(share.id, {
      onSuccess: (response) => {
        toast.success(response?.message || 'Route deleted successfully')
      },
      onError: (error) => {
        toast.error('Unable to delete route', {
          description: getDeliveryShareErrorMessage(error),
        })
      },
    })
  }

  const handleResend: OnResend = (share, recipient, channel = recipient?.channel) => {
    const payload = getDeliveryShareRecipientSendPayload(share, recipient, channel)

    if (!payload) {
      return
    }

    sendDeliveryShare.mutate(payload, {
      onSuccess: (response) => {
        toast.success(response?.message || 'Route link sent.')
      },
      onError: (error) => {
        toast.error('Unable to send route', {
          description: getDeliveryShareErrorMessage(error),
        })
      },
    })
  }

  const createRouteLink = isDateFiltered
    ? path.deliveryRoutes.create().search({ due: selectedDueDate }).link
    : path.deliveryRoutes.create().link
  const routeCardProps: RouteCardProps = {
    deletePendingId: getDeletePendingId(revokeDeliveryShare),
    isReadOnly,
    onCopy: handleCopy,
    onDelete: handleDelete,
    onResend: handleResend,
    resendPendingKey,
    viewLinkProps: (share) => path.deliveryRoutes.show(share.id).link,
  }

  return (
    <div className="w-full max-w-6xl space-y-6 pb-10">
      <AdminPageHeader
        icon={RouteIcon}
        title="Deliveries"
        actions={
          <div
            data-slot="delivery-route-header-actions"
            className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-nowrap sm:items-end"
          >
            <div className="space-y-1.5">
              <Typography variant="label" tone="muted" className="block">
                Route starts on
              </Typography>
              <DeliveryRouteDatePicker
                value={selectedDueDate}
                onChange={handleDateChange}
                disabled={isReadOnly}
                className="w-full sm:w-[180px]"
              />
            </div>

            <AdminHeaderActionButton
              className="w-full justify-center sm:w-auto"
              disabled={isReadOnly}
              asChild
            >
              <Link {...createRouteLink}>
                <RouteIcon className="size-4" />
                Create Route
              </Link>
            </AdminHeaderActionButton>
          </div>
        }
      />

      {isDateFiltered ? (
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="link" onClick={handleClearDateFilter}>
                <Badge>
                  <X className="mr-1" />
                  <span className="font-bold">
                    Route starts on: {formatDeliveryRouteDate(selectedDueDate)}
                  </span>
                </Badge>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove filter</TooltipContent>
          </Tooltip>
        </div>
      ) : null}

      {focusedOrder ? (
        <InfoNotice
          title={
            <span>
              Filtered to{' '}
              <DeliveryRouteOrderLink
                order={focusedOrder}
                searchParams={undefined}
                className="font-semibold text-foreground hover:text-interaction"
              >
                {getDeliveryRouteOrderLabel(focusedOrder)}
              </DeliveryRouteOrderLink>
            </span>
          }
          icon={<AlertCircleIcon />}
        >
          {isDateFiltered
            ? `This view is showing routes for the selected order on ${formatDeliveryRouteDate(selectedDueDate)}.`
            : 'This view is showing routes for the selected order across past delivery dates, current routes, upcoming routes, routes without a delivery date, and recently completed routes.'}
        </InfoNotice>
      ) : null}

      {isDateFiltered && filteredRoutes.length > 0 ? (
        <RouteCardList shares={filteredRoutes} cardProps={routeCardProps} />
      ) : null}

      {isDateFiltered && filteredRoutes.length === 0 ? (
        <EmptyState
          size="compact"
          title={`No deliveries for ${formatDeliveryRouteDate(selectedDueDate)}`}
          description="There are no active delivery routes for this date yet."
        />
      ) : null}

      {!isDateFiltered && routeSections.length > 0 ? (
        <div className="space-y-8">
          {routeSections.map((section) => (
            <RouteSection
              key={section.key}
              title={section.title}
              description={section.description}
              shares={section.shares}
              cardProps={routeCardProps}
              tone={section.tone}
            />
          ))}
        </div>
      ) : null}

      {!isDateFiltered && routeSections.length === 0 ? (
        <EmptyState
          size="compact"
          title="No deliveries to show right now"
          description="There are no routes with past delivery dates, current routes, upcoming routes, routes without a delivery date, or recently completed routes yet."
        />
      ) : null}
    </div>
  )
}
